// Copyright 2021 Sorama B.V.

import { autoinject } from "aurelia-framework";
import { ImageDTO, LinkedMediaObjectType } from "datatypes/SoramaWebApi";
import { BlobExtensions } from "extensions/BlobExtensions";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";
import { TransformedPagedRequest } from "libraries/WebRequestHandler/TransformedPagedRequest";
import { ILinkedMediaObjectRepository } from "repositories/LinkedMediaObjectRepository";
import { AddedImage, IImageService, Image, ImageStorageType, NewImage } from "services/Image/IImageService";

@autoinject
export class CloudImageService implements IImageService {
    constructor(private readonly mediaObjectRepository: ILinkedMediaObjectRepository) {}

    public getImages(queryFilter?: string): IPagedRequest<Image> {
        const sourceRequest = this.mediaObjectRepository.getImages(queryFilter);
        return TransformedPagedRequest.create(sourceRequest, this.toImage);
    }

    public getImage(id: string): Promise<Image> {
        return this.mediaObjectRepository.get(id).then((x) => {
            if (x.type != LinkedMediaObjectType.Image) {
                throw new Error("Image was not found");
            }

            return this.toImage(x as ImageDTO);
        });
    }

    public deleteImage(id: string): Promise<void> {
        return this.mediaObjectRepository.delete(id);
    }

    public getImageUri(image: Image): Promise<string> {
        return this.mediaObjectRepository.getData(image.id).then((x) => {
            return BlobExtensions.toBase64Uri(x);
        });
    }

    public createImage(image: NewImage): Promise<AddedImage> {
        // Strip off the uri header
        const headerRegex = /data:.*;base64,/;
        const regexMatch = headerRegex.exec(image.data);
        const base64 = regexMatch ? image.data.substr(regexMatch[0].length) : image.data;

        image.data = base64;
        return this.mediaObjectRepository.createImage(image).then((x) => {
            const addedImage = this.toImage(x.mediaObject) as AddedImage;
            addedImage.added = x.added;
            return addedImage;
        });
    }

    private toImage(image: ImageDTO): Image {
        return {
            id: image.id,
            dimension: image.dimension,
            name: image.name,
            description: image.description,
            preview: image.preview,
            created: image.created,
            type: ImageStorageType.Cloud,
        };
    }
}
